import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaRupeeSign, FaCalendarAlt, FaPercent, FaEye, FaDownload } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const MyInvestments = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/investments/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data.investments);
    } catch (error) {
      console.error('Error fetching investments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'matured': return 'bg-blue-100 text-blue-800';
      case 'withdrawn': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'pending': return 'Under Review';
      case 'matured': return 'Matured';
      case 'withdrawn': return 'Withdrawn';
      default: return status;
    }
  };

  const filteredInvestments = investments.filter(inv => {
    if (filter === 'all') return true;
    return inv.status === filter;
  });

  const totalInvested = investments.reduce((sum, inv) => sum + inv.amount, 0);
  const totalReturns = investments.reduce((sum, inv) => sum + inv.profitEarned, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Investments - MilkDairy</title>
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">My Investments</h1>
            <p className="text-gray-600 mt-1">Track and manage all your investments</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
              <p className="text-sm opacity-90">Total Invested Amount</p>
              <p className="text-3xl font-bold mt-1">₹{totalInvested.toLocaleString()}</p>
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <p className="text-sm opacity-90">Total Returns Earned</p>
              <p className="text-3xl font-bold mt-1">₹{totalReturns.toLocaleString()}</p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6 border-b">
            {['all', 'active', 'pending', 'matured'].map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 font-medium transition ${filter === tab ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Investments List */}
          {filteredInvestments.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <div className="text-gray-400 mb-4">
                <FaRupeeSign className="text-5xl mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Investments Yet</h3>
              <p className="text-gray-500 mb-6">Start your investment journey with MilkDairy today</p>
              <Link to="/investor/plans" className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-green-700 transition inline-block">
                Explore Investment Plans
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInvestments.map((investment) => (
                <div key={investment._id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
                  <div className="p-6">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 capitalize">{investment.plan} Plan</h3>
                        <p className="text-gray-500 text-sm mt-1">Investment ID: #{investment._id.slice(-8)}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(investment.status)}`}>
                        {getStatusText(investment.status)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-gray-500 text-sm">Amount</p>
                        <p className="text-lg font-semibold text-gray-800">₹{investment.amount.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Returns</p>
                        <p className="text-lg font-semibold text-green-600">{investment.interestRate}% p.a.</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Investment Date</p>
                        <p className="text-gray-800">{new Date(investment.investmentDate).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Maturity Date</p>
                        <p className="text-gray-800">{new Date(investment.maturityDate).toLocaleDateString()}</p>
                      </div>
                    </div>
                    
                    {investment.status === 'active' && (
                      <div className="mt-4 pt-4 border-t flex justify-between items-center">
                        <div>
                          <p className="text-sm text-gray-500">Profit Earned So Far</p>
                          <p className="text-xl font-bold text-green-600">₹{investment.profitEarned.toLocaleString()}</p>
                        </div>
                        <div className="flex gap-3">
                          <button className="text-gray-500 hover:text-green-600 transition">
                            <FaEye /> View Details
                          </button>
                          <button className="text-gray-500 hover:text-green-600 transition">
                            <FaDownload /> Statement
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyInvestments;